/**
 * Axios Interceptors for Background Sync
 *
 * Automatically queues failed POST/PUT/PATCH/DELETE requests when offline
 * for retry when connection is restored
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { backgroundSync } from './backgroundSync';

/**
 * Check if error is due to network issues
 */
function isNetworkError(error: AxiosError): boolean {
  return (
    !error.response && // No response from server
    (error.code === 'ERR_NETWORK' || // Network error code
      error.code === 'ECONNABORTED' || // Connection aborted
      error.message === 'Network Error' || // Generic network error
      !navigator.onLine) // Browser reports offline
  );
}

/**
 * Check if request should be queued for background sync
 * Only queue mutation requests (POST, PUT, PATCH, DELETE)
 */
function shouldQueueRequest(config: InternalAxiosRequestConfig): boolean {
  const method = config.method?.toUpperCase();
  return (
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE'
  );
}

/**
 * Setup axios interceptors for background sync
 */
export function setupBackgroundSyncInterceptors() {
  // Response interceptor to catch network errors
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config;

      // Only queue if it's a network error and a mutation request
      if (config && isNetworkError(error) && shouldQueueRequest(config)) {
        console.log(
          `[BackgroundSync] Network error detected, queueing request: ${config.method?.toUpperCase()} ${
            config.url
          }`
        );

        try {
          // Queue the request for retry
          await backgroundSync.queueRequest(
            config.url || '',
            config.method || 'GET',
            {
              headers: config.headers as Record<string, string>,
              body: config.data,
            }
          );

          // Return a custom error indicating the request was queued
          return Promise.reject({
            ...error,
            queued: true,
            message: 'Request queued for sync when online',
          });
        } catch (queueError) {
          console.error('[BackgroundSync] Failed to queue request:', queueError);
        }
      }

      // Re-throw original error if not queued
      return Promise.reject(error);
    }
  );

  console.log('[BackgroundSync] Axios interceptors configured');
}

/**
 * Initialize background sync on app startup
 */
export function initializeBackgroundSync() {
  setupBackgroundSyncInterceptors();

  // Log initialization
  console.log('[BackgroundSync] Background sync initialized');

  // Try to sync any existing queued requests
  if (navigator.onLine) {
    backgroundSync.sync().catch((error) => {
      console.error('[BackgroundSync] Initial sync failed:', error);
    });
  }
}
